import { Handle, Position } from 'reactflow';
import { FlowNode } from '../types';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

interface CustomNodeProps {
  data: {
    label: string;
    flowNode: FlowNode;
    onEdit: (node: FlowNode) => void;
    onDelete: (nodeId: string) => void;
  };
  selected: boolean;
}

const CustomNode = ({ data, selected }: CustomNodeProps) => {
  const { flowNode, onEdit, onDelete } = data;

  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'message': return '💬';
      case 'question': return '❓';
      case 'input': return '✏️';
      case 'condition': return '🔀';
      case 'end': return '🏁';
      case 'start': return '🚀';
      default: return '⚪';
    }
  };

  const getNodeColor = (type: string) => {
    switch (type) {
      case 'message': return 'bg-gray-100 border-gray-300';
      case 'question': return 'bg-blue-100 border-blue-300';
      case 'input': return 'bg-green-100 border-green-300';
      case 'condition': return 'bg-yellow-100 border-yellow-300';
      case 'end': return 'bg-red-100 border-red-300';
      case 'start': return 'bg-purple-100 border-purple-300';
      default: return 'bg-gray-100 border-gray-300';
    }
  };

  const previweText = (text?: string) => {
    if (!text) return '';
    return text.length > 50 ? text.substring(0, 50) + '...' : text;
  };

  return (
    <div className={`
      min-w-48 max-w-64 bg-white border-2 rounded-lg shadow-sm hover:shadow-md transition-all
      ${getNodeColor(flowNode.type)}
      ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
    `}>
      {/* Input Handle */}
      {flowNode.type !== 'start' && (
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 border-2 border-primary-500 bg-white"
        />
      )}

      {/* Node Content */}
      <div className="p-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-lg">{getNodeIcon(flowNode.type)}</span>
            <span className="text-xs font-medium text-gray-600 uppercase">
              {flowNode.type}
            </span>
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={() => onEdit(flowNode)}
              className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
              title="Editar nó"
            >
              <PencilIcon className="w-3 h-3" />
            </button>
            
            <button
              onClick={() => onDelete(flowNode.id)}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
              title="Excluir nó"
            >
              <TrashIcon className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Title */}
        {flowNode.title && (
          <h4 className="font-medium text-sm text-gray-900 mb-2 line-clamp-2">
            {flowNode.title}
          </h4>
        )}

        {/* Text Preview */}
        {flowNode.text && (
          <p className="text-xs text-gray-600 mb-2 line-clamp-3">
            {previweText(flowNode.text)}
          </p>
        )}

        {/* Options Preview */}
        {flowNode.options && flowNode.options.length > 0 && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">{flowNode.options.length} opção(ões):</span>
            <ul className="mt-1 space-y-1">
              {flowNode.options.slice(0, 3).map((option, index) => (
                <li key={index} className="truncate">
                  • {option.label}
                </li>
              ))}
              {flowNode.options.length > 3 && (
                <li className="font-medium">
                  +{flowNode.options.length - 3} mais...
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Conditions Preview */}
        {flowNode.conditions && flowNode.conditions.length > 0 && (
          <div className="text-xs text-gray-500">
            <span className="font-medium">{flowNode.conditions.length} condição(ões):</span>
            <ul className="mt-1 space-y-1">
              {flowNode.conditions.slice(0, 2).map((condition, index) => (
                <li key={index} className="truncate">
                  • {condition.variable} {condition.operator}
                </li>
              ))}
              {flowNode.conditions.length > 2 && (
                <li className="font-medium">
                  +{flowNode.conditions.length - 2} mais...
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Variable Info */}
        {flowNode.variable && (
          <div className="text-xs text-purple-600 mt-2">
            <span className="font-medium">💾 Variável:</span> {flowNode.variable}
          </div>
        )}
      </div>

      {/* Output Handle */}
      {flowNode.type !== 'end' && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-3 h-3 border-2 border-primary-500 bg-white"
        />
      )}
    </div>
  );
};

export default CustomNode;